// Matches DB table: user_type
export type UserRole =
  // Site-level (internal platform team)
  | 'site_owner'
  | 'site_admin'
  | 'site_dev'
  // Service-level (professionals managing client accounts)
  | 'service_admin'
  | 'service_editor'
  // Module-level (granular per-module permissions)
  | 'service_module_editor'
  | 'service_module_admin'
  // Account-level (business owners and their staff)
  | 'account_owner'
  | 'end_user'
  // Franchise partner (territory operator — accountant-like access, royalty-billed)
  | 'franchisee'
  // Support roles
  | 'support_hero'
  | 'support_manager';

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
  avatar?: string;
  company?: string;
  createdAt: string;
}

// Roles that access the Admin Dashboard (/admin)
export const ADMIN_ROLES: UserRole[] = [
  'site_owner',
  'site_admin',
  'site_dev',
  'service_admin',
  'service_editor',
  'service_module_editor',
  'service_module_admin',
  'support_hero',
  'support_manager',
];

// Roles that access the User Dashboard (/dashboard)
export const USER_ROLES: UserRole[] = [
  'account_owner',
  'end_user',
  'franchisee',
];

export const isAdminRole = (role: UserRole): boolean => {
  return ADMIN_ROLES.includes(role);
};

// =============================================
// Role Hierarchy
// Mirrors hierarchy_level in the user_type DB table
// 1 = highest (Site Owner), 7 = lowest (End User)
// Same level = equal rank
// To edit: change hierarchy_level in Data & Backups > User Types
// =============================================

/** Map from UserRole code to { level, label, typeId } — matches user_type table */
const ROLE_MAP: Record<UserRole, { level: number; label: string; typeId: number }> = {
  site_owner:            { level: 1, label: 'Site Owner',            typeId: 1 },
  site_admin:            { level: 2, label: 'Site Admin',            typeId: 2 },
  site_dev:              { level: 2, label: 'Site Dev',              typeId: 3 },
  service_admin:         { level: 3, label: 'Service Admin',         typeId: 4 },
  support_manager:       { level: 3, label: 'Support Manager',       typeId: 10 },
  service_editor:        { level: 4, label: 'Service Editor',        typeId: 5 },
  support_hero:          { level: 4, label: 'Support Hero',          typeId: 11 },
  service_module_admin:  { level: 5, label: 'Service Module Admin',  typeId: 6 },
  service_module_editor: { level: 5, label: 'Service Module Editor', typeId: 7 },
  account_owner:         { level: 6, label: 'Account Owner',         typeId: 8 },
  franchisee:            { level: 6, label: 'Franchisee',            typeId: 12 },
  end_user:              { level: 7, label: 'End User',              typeId: 9 },
};

/** Get the user_type_id for a role (matches user_type table PK) */
export const getUserTypeId = (role: UserRole): number => ROLE_MAP[role]?.typeId ?? 0;

/** Get the numeric hierarchy level for a role (1 = highest) */
export const getRoleLevel = (role: UserRole): number => {
  return ROLE_MAP[role]?.level ?? 99;
};

/** Get display name for a role */
export const getRoleLabel = (role: UserRole): string => {
  return ROLE_MAP[role]?.label ?? role.replace(/_/g, ' ');
};

/** Get all roles that are below the given role (for "View As" dropdown) */
export const getRolesBelow = (role: UserRole): { role: UserRole; label: string }[] => {
  const myLevel = getRoleLevel(role);
  return (Object.entries(ROLE_MAP) as [UserRole, { level: number; label: string }][])
    .filter(([_, info]) => info.level > myLevel)
    .sort((a, b) => a[1].level - b[1].level)
    .map(([r, info]) => ({ role: r, label: info.label }));
};
